import { fabric } from 'fabric';
import type { MutableRefObject } from 'react';
import Vec2 from 'vec2';

import { CANVAS_CURSOR } from '~/core/constants';
import { DRAGGING_STATE } from '~/core/enums';
import { ApplianceMoveResult } from '~/core/hooks/useAppliance';
import { DrawingAppliance, DrawingShape } from '~/core/models';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Discriminated movement type for cleaner branching logic.
 */
export type MovementType =
  | 'canvas'
  | 'shape'
  | 'appliance-moved'
  | 'appliance-clicked';

/**
 * Parameters for determining movement type.
 */
export interface MovementContext {
  movingItem: DrawingShape | string | undefined;
  moveVec: Vec2;
}

/**
 * Parameters for resetting dragging state at end of mouse up.
 */
export interface DraggingResetParams {
  draggingStateRef: MutableRefObject<DRAGGING_STATE>;
  movingItemRef: MutableRefObject<DrawingShape | string | undefined>;
  canvas: fabric.Canvas;
}

/**
 * Parameters for animating appliance back to original position.
 */
export interface AnimateBackParams {
  fabricObj: fabric.Object;
  result: ApplianceMoveResult;
  canvas: fabric.Canvas;
}

/**
 * Parameters for updating appliance position after successful move.
 */
export interface UpdatePositionParams {
  fabricObj: fabric.Object;
  appliance: DrawingAppliance;
}

// ============================================================================
// MOVEMENT TYPE DETECTION
// ============================================================================

/**
 * Determines the type of movement being completed based on movingItemRef value.
 *
 * Movement types:
 * - 'canvas': No item being moved (canvas pan only)
 * - 'shape': movingItemRef is a DrawingShape object
 * - 'appliance-moved': movingItemRef is a string (appliance ID) with actual movement
 * - 'appliance-clicked': movingItemRef is a string but no movement occurred
 */
export const determineMovementType = ({
  movingItem,
  moveVec,
}: MovementContext): MovementType => {
  if (!movingItem) {
    return 'canvas';
  }

  if (typeof movingItem === 'object') {
    return 'shape';
  }

  // movingItem is a string (appliance ID)
  const hasMovement = moveVec.x !== 0 || moveVec.y !== 0;

  return hasMovement ? 'appliance-moved' : 'appliance-clicked';
};

// ============================================================================
// PREDICATES
// ============================================================================

/**
 * Checks if canvas should auto-pan after dragging completes.
 * Only auto-pans when in auto-zoom mode (not manual zoom).
 */
export const shouldAutoPanAfterDrag = (
  draggingState: DRAGGING_STATE,
  movingItem: DrawingShape | string | undefined,
  isManualZoom: boolean
): boolean =>
  draggingState === DRAGGING_STATE.DRAGGING && !movingItem && !isManualZoom;

/**
 * Checks if the appliance move failed and should animate back.
 */
export const shouldAnimateBack = (
  fabricObj: fabric.Object | undefined,
  result: ApplianceMoveResult
): fabricObj is fabric.Object =>
  Boolean(fabricObj && result.appliance && !result.success);

/**
 * Checks if the appliance move succeeded and should update position.
 */
export const shouldUpdatePosition = (
  fabricObj: fabric.Object | undefined,
  result: ApplianceMoveResult
): fabricObj is fabric.Object =>
  Boolean(fabricObj && result.appliance && result.success);

// ============================================================================
// FABRIC OBJECT HELPERS
// ============================================================================

/**
 * Finds a fabric object by its name property.
 */
export const findFabricObjectByName = (
  canvas: fabric.Canvas,
  name: string
): fabric.Object | undefined =>
  canvas.getObjects().find((obj) => obj.name === name);

/**
 * Animates an appliance back to its original position when move fails.
 * Provides smooth 150ms animation for better UX than instant snap-back.
 */
export const animateApplianceBack = ({
  fabricObj,
  result,
  canvas,
}: AnimateBackParams): void => {
  // Get target position - fallback to current fabric object position if neither source available
  const targetX =
    result.originalPosition?.x ??
    result.appliance?.centerPoint.x ??
    (fabricObj.left as number);
  const targetY =
    result.originalPosition?.y ??
    result.appliance?.centerPoint.y ??
    (fabricObj.top as number);

  fabricObj.animate(
    { left: targetX, top: targetY },
    {
      duration: 150,
      onChange: () => canvas.requestRenderAll(),
      onComplete: () => {
        fabricObj.setCoords();
        canvas.requestRenderAll();
      },
    }
  );
};

/**
 * Updates fabric object position after successful appliance move.
 */
export const updateAppliancePosition = ({
  fabricObj,
  appliance,
}: UpdatePositionParams): void => {
  fabricObj.set({
    left: appliance.centerPoint.x,
    top: appliance.centerPoint.y,
  });
  fabricObj.setCoords();
};

/**
 * Keeps an appliance selected after movement and triggers render.
 */
export const keepApplianceSelected = (
  canvas: fabric.Canvas,
  fabricObj: fabric.Object | undefined
): void => {
  if (fabricObj) {
    canvas.setActiveObject(fabricObj as fabric.Image);
    canvas.requestRenderAll();
  }
};

// ============================================================================
// STATE RESET
// ============================================================================

/**
 * Resets dragging state and cursor at the end of mouse up.
 * MUST be called at the end of EVERY mouse up, regardless of movement type.
 */
export const resetDraggingState = ({
  draggingStateRef,
  movingItemRef,
  canvas,
}: DraggingResetParams): void => {
  draggingStateRef.current = DRAGGING_STATE.IDLE;
  movingItemRef.current = undefined;
  canvas.defaultCursor = CANVAS_CURSOR.DEFAULT.HOVER;
};
